import { CloseOutlined, LoadingOutlined, MinusOutlined, PlusOutlined } from "@ant-design/icons";
import {
  FeatureFlag,
  GetSubscriptionInfo200Response,
  SubscriptionScheduledChangeType,
  SubscriptionUpdateType,
} from "@coeff/api";
import { useFeatureGate } from "@statsig/react-bindings";
import { useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";

import { BContext } from "../../../types";
import { planNameFromType } from "../../../utils";
import { BillingContext } from "../Billing";
import { isPlanMember } from "../billing.utils";
import { assignableSeats, getPendingLicenseDrop } from "../pagesV2/members.utils";

import { ContactSalesModal } from "./ContactSalesModal";
import { BILLING_TOKENS as C } from "./designTokens";

/* Self-contained Add/Remove seats dialog matching the Members design (stepper, unit price,
 * cost breakdown, self-serve cap note). Reads actions from BillingContext; operates on the
 * loaded subscription by default, or on any other plan via the `subInfo` override. */

const money = (n?: number) =>
  `$${(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;
const ModalCard = styled.div`
  width: 600px;
  max-width: calc(100vw - 40px);
  background: ${C.surface};
  border-radius: 12px;
  box-shadow: 0 3px 18px rgba(0, 0, 0, 0.18);
  padding: 26px 30px 30px;
  color: ${C.text};
`;
const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  .title {
    font-size: 22px;
    font-weight: 800;
    color: ${C.heading};
  }
  .sub {
    font-size: 13px;
    color: ${C.placeholder};
    margin-top: 4px;
  }
  .close {
    border: none;
    background: none;
    cursor: pointer;
    color: ${C.placeholder};
    font-size: 16px;
    &:hover {
      color: ${C.text};
    }
  }
`;
const Stepper = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  border: 1px solid ${C.border};
  border-radius: 10px;
  width: fit-content;
  margin: 18px 0 12px;
  button {
    width: 40px;
    height: 40px;
    border: none;
    background: ${C.surface};
    cursor: pointer;
    color: ${C.muted};
    font-size: 14px;
    &:first-child {
      border-right: 1px solid ${C.border};
    }
    &:last-child {
      border-left: 1px solid ${C.border};
    }
    &:disabled {
      color: ${C.border};
      cursor: not-allowed;
    }
  }
  .n {
    font-size: 17px;
    font-weight: 700;
    width: 52px;
    text-align: center;
  }
`;
const CostBox = styled.div`
  border: 1px solid ${C.divider};
  border-radius: 8px;
  padding: 6px 14px;
  margin: 14px 0;
  .r {
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    color: ${C.muted};
    padding: 9px 0;
    & + .r {
      border-top: 1px solid ${C.divider};
    }
    &.total {
      color: ${C.text};
      font-weight: 700;
    }
  }
`;
const ScheduledBanner = styled.div`
  display: flex;
  align-items: center;
  background: ${C.warnBg};
  color: ${C.warnFg};
  border-radius: 8px;
  padding: 12px 14px;
  margin: 16px 0 4px;
  font-size: 13.5px;
  line-height: 19px;
`;
const PrimaryBtn = styled.button<{ disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 40px;
  padding: 0 16px;
  border: none;
  border-radius: 8px;
  background: ${p => (p.disabled ? C.blueDisabled : C.blue)};
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: ${p => (p.disabled ? "not-allowed" : "pointer")};
  &:hover {
    background: ${p => (p.disabled ? C.blueDisabled : C.blueHover)};
  }
`;
const GhostBtn = styled.button`
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: none;
  font-size: 15px;
  font-weight: 700;
  color: ${C.text};
  cursor: pointer;
  &:hover {
    background: ${C.sunken};
  }
  &:disabled {
    color: ${C.placeholder};
    cursor: not-allowed;
    background: none;
  }
`;

const SpinnerWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 56px 0;
`;
const BodyNote = styled.div`
  margin: 18px 0 22px;
  font-size: 14px;
  color: ${C.muted};
`;
const FootNote = styled.div`
  font-size: 12.5px;
  color: ${C.placeholder};
  margin-bottom: 20px;
`;
const FooterRow = styled.div<{ end?: boolean }>`
  display: flex;
  justify-content: ${p => (p.end ? "flex-end" : "space-between")};
`;

type Props = {
  visible: boolean;
  onClose: () => void;
  /** "add" (default) buys seats now; "remove" drops unassigned seats at the next renewal. */
  mode?: "add" | "remove";
  /** Operate on another plan's subscription instead of the loaded one (admin cross-plan). */
  subInfo?: GetSubscriptionInfo200Response | null;
  /** The target plan's info is still being fetched — show a spinner until it arrives. */
  loading?: boolean;
};

export const AddSeatsModal: React.FC<Props> = ({
  visible,
  onClose,
  mode = "add",
  subInfo,
  loading = false,
}) => {
  const { billingMetadata, userSubscription, updateSubscription, syncBilling } = useContext(
    BillingContext
  ) as BContext;
  const { value: enableSelfServeAiDashboards } = useFeatureGate(
    FeatureFlag.EnableSelfServeAiDashboards
  );
  const queryClient = useQueryClient();

  const [n, setN] = useState(1);
  const [busyAction, setBusyAction] = useState<null | "confirm">(null);
  const busy = busyAction !== null;

  // Refresh both the loaded subscription (redux) and the Members page's per-plan caches.
  const refreshAll = async () => {
    await syncBilling();
    await queryClient.invalidateQueries({ queryKey: ["MembersOtherSubInfo"] });
  };

  useEffect(() => {
    if (visible) {
      setN(1);
    }
  }, [visible]);

  const info = subInfo ?? userSubscription;
  const subscriptionDetails = info?.subscription_details;
  const currentPlan = billingMetadata?.plans.find(
    p =>
      p.plan_type === subscriptionDetails?.plan_type &&
      p.billing_interval === subscriptionDetails?.billing_interval
  );
  const pricePerUser = currentPlan?.per_user_plan?.per_item_monthly_price_usd ?? 0;
  const cap = subscriptionDetails?.max_self_serve_licenses ?? undefined;
  const tierLabel = planNameFromType(subscriptionDetails?.plan_type ?? "pro");
  const maxUsers = subscriptionDetails?.max_users ?? 0;
  const interval = subscriptionDetails?.billing_interval ?? "annually";
  const renewalStr = subscriptionDetails?.next_billing_cycle_dt
    ? moment(subscriptionDetails.next_billing_cycle_dt).format("MMM D, YYYY")
    : "renewal";

  const isRemove = mode === "remove";
  // Seats the org may assign right now (max_users).
  const capacitySeats = subscriptionDetails ? assignableSeats(subscriptionDetails) : maxUsers;
  const seatedUsers = (info?.users_from_domain ?? []).filter(u =>
    isPlanMember(u, subscriptionDetails)
  );
  const invitesCount = info?.invite_users?.length ?? 0;

  // Pending end-of-term seat drop; removals stack onto effectiveSeats, not today's count.
  // Shared with ManageTeam via getPendingLicenseDrop — see members.utils.ts for the rules.
  // effectiveSeats/hasScheduledDrop are ungated; showDropBanner carries the rollout flag.
  const {
    effectiveLicenses: effectiveSeats,
    dropsSeats: hasScheduledDrop,
    showsDropNotice: showDropBanner,
  } = getPendingLicenseDrop(subscriptionDetails);

  // Seats the org can still give up (matches the old Manage Licenses rule: only unassigned ones).
  // Measured against effectiveSeats, since removal lands at renewal — NOT a count of seats that
  // are unusable today: a scheduled removal doesn't put a seat on hold (#14195), it stays
  // assignable until renewal. Clamped because assigned users may already exceed a pending
  // reduction, and the backend's `>= assigned + invited` assert would 500 on a negative.
  const removableSeats = Math.max(effectiveSeats - seatedUsers.length - invitesCount, 0);
  // The self-serve cap is a TOTAL (e.g. 5 monthly-Pro seats), so the addable count is what's
  // left under it — matching the old isAtSelfServeCap contact-sales behavior at the cap.
  const addableSeats = cap != null ? Math.max(cap - capacitySeats, 0) : undefined;
  const maxN = isRemove ? removableSeats : addableSeats;

  // Coupon-aware monthly figures; the pay-today number is a proration estimate based on the
  // time left in the billing cycle — the backend charges the exact amount on confirm.
  const couponPct = billingMetadata?.coupon_discount_percentage ?? 0;
  const unitMonthly = pricePerUser * (1 - couponPct / 100);
  const monthlyDelta = n * unitMonthly;
  const cycleDays = interval === "annually" ? 365 : 30;
  const remainingDays = subscriptionDetails?.next_billing_cycle_dt
    ? Math.max(moment(subscriptionDetails.next_billing_cycle_dt).diff(moment(), "days"), 0)
    : cycleDays;
  const remainingFrac = Math.min(remainingDays / cycleDays, 1);
  const cycleMonths = interval === "annually" ? 12 : 1;
  const todayEstimate = monthlyDelta * cycleMonths * remainingFrac;
  const newMonthlyTotal = (isRemove ? effectiveSeats - n : capacitySeats + n) * unitMonthly;

  const confirm = async () => {
    const seatedUserIds = seatedUsers
      .map(u => u.user.user_id)
      .filter((id): id is string => Boolean(id));
    const inviteEmails = (info?.invite_users ?? []).map(u => u.user.email);

    setBusyAction("confirm");
    try {
      const res = await updateSubscription({
        update_type: SubscriptionUpdateType.ModifySubscription,
        plan: currentPlan,
        // Removals stack onto the pending scheduled count; purchases apply to today's count.
        total_user_licenses: isRemove ? effectiveSeats - n : capacitySeats + n,
        invite_user_emails: inviteEmails,
        selected_user_ids: seatedUserIds,
        selected_data_source_types: subscriptionDetails?.selected_data_source_types,
        coupon_discount_percentage: billingMetadata?.coupon_discount_percentage,
        subscription_id: subscriptionDetails?.subscription_id,
      });
      if (!res.is_success) {
        message.error(res.error_msg || "Something went wrong. Please try again");
        return;
      }
      await refreshAll();
      message.success(
        isRemove
          ? `${n} seat${n === 1 ? "" : "s"} will be removed at renewal`
          : `${n} seat${n === 1 ? "" : "s"} added`
      );
      onClose();
    } finally {
      setBusyAction(null);
    }
  };

  if (!visible) {
    return null;
  }

  // Plans with the AI Dashboards add-on are single-license only (the server also hard-blocks
  // buying more seats). Per the "Adding Licenses" design, the add-seats action opens the
  // Contact Sales modal — the same one the pricing-page ineligible flow uses — rather than the
  // stepper. These subs stay checkout_mode SELF_SERVE (so the owner can cancel the add-on),
  // which is why the sales-managed branch below doesn't catch them.
  if (
    !loading &&
    !isRemove &&
    enableSelfServeAiDashboards &&
    subscriptionDetails?.has_web_dashboard_addon
  ) {
    return (
      <ContactSalesModal
        showModal
        onCancel={onClose}
        hideTextarea
        description={`To add additional users to your ${tierLabel} plan, with AI Dashboards, contact sales.`}
      />
    );
  }

  return (
    <Overlay onClick={busy ? undefined : onClose}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <div>
            <div className="title">{isRemove ? "Remove seats" : "Add seats"}</div>
            {!loading && (
              <div className="sub">
                {tierLabel} plan · {capacitySeats} seats today
              </div>
            )}
          </div>
          <button className="close" disabled={busy} onClick={onClose}>
            <CloseOutlined />
          </button>
        </ModalHeader>

        {loading ? (
          <SpinnerWrap>
            <LoadingOutlined spin style={{ fontSize: 26, color: C.blue }} />
          </SpinnerWrap>
        ) : subscriptionDetails?.checkout_mode === "contact_us" ? (
          // The resolved plan turned out to be sales-managed (possible when opening a
          // cross-plan card before its info finished loading).
          <>
            <BodyNote>
              This plan is managed by our sales team. Please contact{" "}
              <a href="mailto:support@coefficient.io" style={{ color: C.blue }}>
                support@coefficient.io
              </a>{" "}
              to make changes.
            </BodyNote>
            <FooterRow end>
              <PrimaryBtn onClick={onClose}>Got it</PrimaryBtn>
            </FooterRow>
          </>
        ) : (
          <>
            {showDropBanner && (
              <ScheduledBanner>
                {/* Same opening fact either way; only the consequence differs. */}
                <span>
                  You have {capacitySeats} seats now, reducing to <b>{effectiveSeats}</b> on{" "}
                  {renewalStr}.{" "}
                  {isRemove
                    ? "Removing more lowers what you'll have then."
                    : "Adding seats adds to what you have now and cancels the scheduled drop."}
                </span>
              </ScheduledBanner>
            )}

            {(isRemove && removableSeats === 0) || (!isRemove && addableSeats === 0) ? (
              <>
                <BodyNote>
                  {isRemove ? (
                    hasScheduledDrop ? (
                      <>
                        You can only remove unassigned seats, and every seat on this plan is
                        currently assigned or already scheduled to be removed.
                      </>
                    ) : (
                      <>
                        You can only remove unassigned seats, and every seat on this plan is
                        currently assigned. Unassign a license first.
                      </>
                    )
                  ) : (
                    <>
                      {tierLabel} plans support up to {cap} user licenses. Please contact{" "}
                      <a href="mailto:sales@coefficient.io" style={{ color: C.blue }}>
                        sales@coefficient.io
                      </a>{" "}
                      for volume pricing.
                    </>
                  )}
                </BodyNote>
                <FooterRow end>
                  <PrimaryBtn onClick={onClose}>Got it</PrimaryBtn>
                </FooterRow>
              </>
            ) : (
              <>
                <Stepper>
                  <button disabled={busy || n <= 1} onClick={() => setN(x => Math.max(1, x - 1))}>
                    <MinusOutlined />
                  </button>
                  <span className="n">{n}</span>
                  <button
                    disabled={busy || Boolean(maxN && n >= maxN)}
                    title={
                      maxN && n >= maxN
                        ? isRemove
                          ? `Only ${removableSeats} unassigned seat${
                              removableSeats === 1 ? "" : "s"
                            } can be removed`
                          : `${tierLabel} plans support up to ${cap} user licenses`
                        : undefined
                    }
                    onClick={() => setN(x => (maxN ? Math.min(maxN, x + 1) : x + 1))}
                  >
                    <PlusOutlined />
                  </button>
                </Stepper>
                {Boolean(maxN && n >= maxN) && (
                  <div style={{ fontSize: 13, color: "#E5484D", margin: "-4px 0 8px" }}>
                    {isRemove
                      ? `Only ${removableSeats} unassigned seat${
                          removableSeats === 1 ? "" : "s"
                        } can be removed — assigned seats must be unassigned first.`
                      : `${tierLabel} plans support up to ${cap} user licenses — contact sales@coefficient.io for volume pricing.`}
                  </div>
                )}
                <div style={{ fontSize: 14, color: C.muted }}>
                  {tierLabel} seats · {money(unitMonthly)}/user/mo
                </div>
                <CostBox>
                  <div className="r">
                    <span>
                      {n} × {tierLabel} seat{isRemove ? " removed" : ""}
                    </span>
                    <span>
                      {isRemove ? "−" : ""}
                      {money(monthlyDelta)} / mo
                    </span>
                  </div>
                  <div className="r">
                    <span>
                      {isRemove
                        ? `Takes effect at renewal · ${renewalStr}`
                        : `Prorated until renewal · ${renewalStr} (est.)`}
                    </span>
                    <span>{money(isRemove ? 0 : todayEstimate)} today</span>
                  </div>
                  <div className="r total">
                    <span>New monthly total from {renewalStr}</span>
                    <span>{money(newMonthlyTotal)}</span>
                  </div>
                </CostBox>
                {/* No proactive cap disclosure (matches the old UI): the self-serve limit only
                surfaces when it's actually hit — via the at-cap state or the stepper error. */}
                {isRemove && (
                  <FootNote>
                    {`${removableSeats} unassigned seat${
                      removableSeats === 1 ? "" : "s"
                    } can be removed. No refunds for the current cycle.`}
                  </FootNote>
                )}
                <FooterRow>
                  <GhostBtn disabled={busy} onClick={onClose}>
                    Cancel
                  </GhostBtn>
                  <PrimaryBtn disabled={busy} onClick={confirm}>
                    {busyAction === "confirm" && <LoadingOutlined spin />}
                    {busyAction === "confirm"
                      ? "Processing…"
                      : isRemove
                      ? "Confirm changes"
                      : `Confirm & pay now · ${money(todayEstimate)}`}
                  </PrimaryBtn>
                </FooterRow>
              </>
            )}
          </>
        )}
      </ModalCard>
    </Overlay>
  );
};
